import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Plus, Trash2, Eye, Save, Loader2 } from "lucide-react";
import OperatorLayout from "./OperatorLayout";

const OperatorPricing = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [activeSection, setActiveSection] = useState<"plans" | "addons">("plans");

  const { data: plans = [], isLoading: plansLoading } = useQuery({
    queryKey: ["operator-craft-plans"],
    queryFn: async () => {
      const { data, error } = await supabase.from("craft_plans").select("*").order("sort_order");
      if (error) throw error;
      return data || [];
    },
  });

  const [editPlan, setEditPlan] = useState<any>(null);

  const savePlanMutation = useMutation({
    mutationFn: async (plan: any) => {
      const record = {
        key: plan.key, name: plan.name, monthly_price_cents: plan.monthly_price_cents,
        included_accepted_leads_per_month: plan.included_accepted_leads_per_month,
        delivery_cap_per_month: plan.delivery_cap_per_month || null,
        radius_max_km: plan.radius_max_km || null, max_trades: plan.max_trades || null,
        features: plan.features || [], is_active: plan.is_active ?? true, sort_order: plan.sort_order || 0,
      };
      if (plan.id) {
        const { error } = await supabase.from("craft_plans").update(record).eq("id", plan.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("craft_plans").insert(record);
        if (error) throw error;
      }
      await supabase.from("audit_log").insert({
        actor_user_id: null, actor_role: "operator", entity_type: "PRICING",
        event_type: plan.id ? "PLAN_UPDATED" : "PLAN_CREATED",
        meta: { plan_key: plan.key, price: plan.monthly_price_cents },
      });
    },
    onSuccess: () => {
      toast.success(t("op.pricPlanSaved"));
      setEditPlan(null);
      queryClient.invalidateQueries({ queryKey: ["operator-craft-plans"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deletePlanMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("craft_plans").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t("op.pricPlanDeleted"));
      queryClient.invalidateQueries({ queryKey: ["operator-craft-plans"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const { data: addons = [], isLoading: addonsLoading } = useQuery({
    queryKey: ["operator-craft-addons"],
    queryFn: async () => {
      const { data, error } = await supabase.from("craft_plan_addons").select("*").order("sort_order");
      if (error) throw error;
      return data || [];
    },
  });

  const [editAddon, setEditAddon] = useState<any>(null);

  const saveAddonMutation = useMutation({
    mutationFn: async (addon: any) => {
      const record = {
        key: addon.key, name: addon.name, price_cents: addon.price_cents,
        accepted_leads_extra: addon.accepted_leads_extra,
        is_active: addon.is_active ?? true, sort_order: addon.sort_order || 0,
      };
      if (addon.id) {
        const { error } = await supabase.from("craft_plan_addons").update(record).eq("id", addon.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("craft_plan_addons").insert(record);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success(t("op.pricAddonSaved"));
      setEditAddon(null);
      queryClient.invalidateQueries({ queryKey: ["operator-craft-addons"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteAddonMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("craft_plan_addons").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t("op.pricAddonDeleted"));
      queryClient.invalidateQueries({ queryKey: ["operator-craft-addons"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (plansLoading || addonsLoading) {
    return (
      <OperatorLayout title={t("op.pricTitle")} subtitle={t("op.pricSubtitle")}>
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-accent" /></div>
      </OperatorLayout>
    );
  }

  return (
    <OperatorLayout title={t("op.pricTitle")} subtitle={t("op.pricSubtitle")}>
      <div className="flex gap-1 border-b border-border mb-8">
        {[
          { key: "plans" as const, label: t("op.pricPlans") },
          { key: "addons" as const, label: t("op.pricAddons") },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveSection(tab.key)}
            className={`px-4 py-3 text-sm border-b-2 transition-colors ${
              activeSection === tab.key
                ? "border-accent text-foreground font-medium"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {tab.label}
          </button>
        ))}
        <a href="/craft/pricing" target="_blank" rel="noopener noreferrer"
          className="ml-auto flex items-center gap-1 px-4 py-3 text-sm text-muted-foreground hover:text-foreground">
          <Eye className="w-4 h-4" /> {t("op.pricPreview")}
        </a>
      </div>

      {activeSection === "plans" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-subheadline">{t("op.pricPlans")}</h3>
            <Button variant="quiet" size="sm" onClick={() => setEditPlan({
              key: "", name: "", monthly_price_cents: 0, included_accepted_leads_per_month: 5,
              delivery_cap_per_month: 30, radius_max_km: 20, max_trades: 1, features: [], is_active: true, sort_order: 0,
            })}>
              <Plus className="w-4 h-4 mr-1" /> {t("op.pricNewPlan")}
            </Button>
          </div>

          {editPlan && (
            <div className="p-6 border border-accent/30 bg-card space-y-4">
              <h4 className="text-body font-medium">{editPlan.id ? t("op.pricEditPlan") : t("op.pricNewPlan")}</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-micro block mb-1">{t("op.pricKey")}</label>
                  <input value={editPlan.key} onChange={(e) => setEditPlan({ ...editPlan, key: e.target.value })}
                    className="w-full h-9 px-3 border border-border bg-background text-sm" placeholder="basic" />
                </div>
                <div>
                  <label className="text-micro block mb-1">{t("op.pricName")}</label>
                  <input value={editPlan.name} onChange={(e) => setEditPlan({ ...editPlan, name: e.target.value })}
                    className="w-full h-9 px-3 border border-border bg-background text-sm" placeholder="Basic" />
                </div>
                <div>
                  <label className="text-micro block mb-1">{t("op.pricPriceCents")}</label>
                  <input type="number" value={editPlan.monthly_price_cents}
                    onChange={(e) => setEditPlan({ ...editPlan, monthly_price_cents: Number(e.target.value) })}
                    className="w-full h-9 px-3 border border-border bg-background text-sm" />
                </div>
                <div>
                  <label className="text-micro block mb-1">{t("op.pricInclLeads")}</label>
                  <input type="number" value={editPlan.included_accepted_leads_per_month}
                    onChange={(e) => setEditPlan({ ...editPlan, included_accepted_leads_per_month: Number(e.target.value) })}
                    className="w-full h-9 px-3 border border-border bg-background text-sm" />
                </div>
                <div>
                  <label className="text-micro block mb-1">{t("op.pricMaxDeliveries")}</label>
                  <input type="number" value={editPlan.delivery_cap_per_month || ""}
                    onChange={(e) => setEditPlan({ ...editPlan, delivery_cap_per_month: e.target.value ? Number(e.target.value) : null })}
                    className="w-full h-9 px-3 border border-border bg-background text-sm" />
                </div>
                <div>
                  <label className="text-micro block mb-1">{t("op.pricMaxRadius")}</label>
                  <input type="number" value={editPlan.radius_max_km || ""}
                    onChange={(e) => setEditPlan({ ...editPlan, radius_max_km: e.target.value ? Number(e.target.value) : null })}
                    className="w-full h-9 px-3 border border-border bg-background text-sm" />
                </div>
                <div>
                  <label className="text-micro block mb-1">{t("op.pricMaxTrades")}</label>
                  <input type="number" value={editPlan.max_trades || ""}
                    onChange={(e) => setEditPlan({ ...editPlan, max_trades: e.target.value ? Number(e.target.value) : null })}
                    className="w-full h-9 px-3 border border-border bg-background text-sm" />
                </div>
                <div>
                  <label className="text-micro block mb-1">{t("op.pricSort")}</label>
                  <input type="number" value={editPlan.sort_order}
                    onChange={(e) => setEditPlan({ ...editPlan, sort_order: Number(e.target.value) })}
                    className="w-full h-9 px-3 border border-border bg-background text-sm" />
                </div>
              </div>
              <div>
                <label className="text-micro block mb-1">{t("op.pricFeaturesJSON")}</label>
                <textarea
                  value={JSON.stringify(editPlan.features || [], null, 2)}
                  onChange={(e) => { try { setEditPlan({ ...editPlan, features: JSON.parse(e.target.value) }); } catch {} }}
                  rows={4}
                  className="w-full px-3 py-2 border border-border bg-background text-sm font-mono"
                />
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={editPlan.is_active}
                    onChange={(e) => setEditPlan({ ...editPlan, is_active: e.target.checked })} className="w-4 h-4" />
                  {t("op.pricActiveLabel")}
                </label>
                <div className="flex-1" />
                <Button variant="quiet" size="sm" onClick={() => setEditPlan(null)}>{t("common.cancel")}</Button>
                <Button variant="commit" size="sm" onClick={() => savePlanMutation.mutate(editPlan)} disabled={savePlanMutation.isPending}>
                  <Save className="w-4 h-4 mr-1" /> {t("common.save")}
                </Button>
              </div>
            </div>
          )}

          <div className="border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50">
                <tr>
                  <th className="text-left px-4 py-3 text-micro">Plan</th>
                  <th className="text-right px-4 py-3 text-micro">{t("op.colPrice")}</th>
                  <th className="text-right px-4 py-3 text-micro">{t("op.pricInclLeads")}</th>
                  <th className="text-right px-4 py-3 text-micro">{t("op.pricMaxDeliveries")}</th>
                  <th className="text-center px-4 py-3 text-micro">{t("op.colStatus")}</th>
                  <th className="text-right px-4 py-3 text-micro">{t("op.pricEdit")}</th>
                </tr>
              </thead>
              <tbody>
                {plans.map((p: any) => (
                  <tr key={p.id} className="border-t border-border">
                    <td className="px-4 py-3 font-medium">{p.name} <span className="text-muted-foreground">({p.key})</span></td>
                    <td className="px-4 py-3 text-right price-display">{(p.monthly_price_cents / 100).toFixed(0)} €</td>
                    <td className="px-4 py-3 text-right">{p.included_accepted_leads_per_month}</td>
                    <td className="px-4 py-3 text-right">{p.delivery_cap_per_month || "—"}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-xs px-2 py-0.5 ${p.is_active ? "bg-accent/10 text-accent" : "bg-muted text-muted-foreground"}`}>
                        {p.is_active ? t("op.pricActiveLabel") : t("op.pricInactive")}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="quiet" size="sm" onClick={() => setEditPlan({ ...p })}>{t("op.pricEdit")}</Button>
                        <Button variant="quiet" size="sm"
                          onClick={() => { if (confirm(t("op.pricDeleteConfirm"))) deletePlanMutation.mutate(p.id); }}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeSection === "addons" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-subheadline">{t("op.pricAddons")}</h3>
            <Button variant="quiet" size="sm"
              onClick={() => setEditAddon({ key: "", name: "", price_cents: 0, accepted_leads_extra: 10, is_active: true, sort_order: 0 })}>
              <Plus className="w-4 h-4 mr-1" /> {t("op.pricNewAddon")}
            </Button>
          </div>

          {editAddon && (
            <div className="p-6 border border-accent/30 bg-card space-y-4">
              <h4 className="text-body font-medium">{editAddon.id ? t("op.pricEditAddon") : t("op.pricNewAddon")}</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-micro block mb-1">{t("op.pricKey")}</label>
                  <input value={editAddon.key} onChange={(e) => setEditAddon({ ...editAddon, key: e.target.value })}
                    className="w-full h-9 px-3 border border-border bg-background text-sm" />
                </div>
                <div>
                  <label className="text-micro block mb-1">{t("op.pricName")}</label>
                  <input value={editAddon.name} onChange={(e) => setEditAddon({ ...editAddon, name: e.target.value })}
                    className="w-full h-9 px-3 border border-border bg-background text-sm" />
                </div>
                <div>
                  <label className="text-micro block mb-1">{t("op.pricPriceCents")}</label>
                  <input type="number" value={editAddon.price_cents}
                    onChange={(e) => setEditAddon({ ...editAddon, price_cents: Number(e.target.value) })}
                    className="w-full h-9 px-3 border border-border bg-background text-sm" />
                </div>
                <div>
                  <label className="text-micro block mb-1">{t("op.pricExtraLeads")}</label>
                  <input type="number" value={editAddon.accepted_leads_extra}
                    onChange={(e) => setEditAddon({ ...editAddon, accepted_leads_extra: Number(e.target.value) })}
                    className="w-full h-9 px-3 border border-border bg-background text-sm" />
                </div>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={editAddon.is_active}
                    onChange={(e) => setEditAddon({ ...editAddon, is_active: e.target.checked })} className="w-4 h-4" />
                  {t("op.pricActiveLabel")}
                </label>
                <div className="flex-1" />
                <Button variant="quiet" size="sm" onClick={() => setEditAddon(null)}>{t("common.cancel")}</Button>
                <Button variant="commit" size="sm" onClick={() => saveAddonMutation.mutate(editAddon)} disabled={saveAddonMutation.isPending}>
                  <Save className="w-4 h-4 mr-1" /> {t("common.save")}
                </Button>
              </div>
            </div>
          )}

          <div className="border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50">
                <tr>
                  <th className="text-left px-4 py-3 text-micro">Add-on</th>
                  <th className="text-right px-4 py-3 text-micro">{t("op.colPrice")}</th>
                  <th className="text-right px-4 py-3 text-micro">{t("op.pricExtraLeads")}</th>
                  <th className="text-center px-4 py-3 text-micro">{t("op.colStatus")}</th>
                  <th className="text-right px-4 py-3 text-micro">{t("op.pricEdit")}</th>
                </tr>
              </thead>
              <tbody>
                {addons.map((a: any) => (
                  <tr key={a.id} className="border-t border-border">
                    <td className="px-4 py-3 font-medium">{a.name} <span className="text-muted-foreground">({a.key})</span></td>
                    <td className="px-4 py-3 text-right price-display">{(a.price_cents / 100).toFixed(0)} €</td>
                    <td className="px-4 py-3 text-right">{a.accepted_leads_extra}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-xs px-2 py-0.5 ${a.is_active ? "bg-accent/10 text-accent" : "bg-muted text-muted-foreground"}`}>
                        {a.is_active ? t("op.pricActiveLabel") : t("op.pricInactive")}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="quiet" size="sm" onClick={() => setEditAddon({ ...a })}>{t("op.pricEdit")}</Button>
                        <Button variant="quiet" size="sm"
                          onClick={() => { if (confirm(t("op.pricDeleteConfirm"))) deleteAddonMutation.mutate(a.id); }}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </OperatorLayout>
  );
};

export default OperatorPricing;
